
import React, { useState, useEffect } from 'react';
import { landingPageService } from '../services/landingPageService';
import { generateLandingPageCopy } from '../services/geminiService';
import { storage } from '../lib/storage';
import { LandingPage, Product } from '../types';
import { 
  Sparkles, 
  Plus, 
  Trash2, 
  Eye, 
  ExternalLink, 
  Loader2, 
  X, 
  Check, 
  Megaphone, 
  ArrowRight,
  TrendingUp,
  Layout,
  MousePointer2,
  Copy,
  Edit2
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminLandingPages: React.FC<Props> = ({ storeId }) => {
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    goal: '',
    selectedProductIds: [] as string[],
    themeColor: '#2563eb'
  });

  const [generatedLP, setGeneratedLP] = useState<LandingPage | null>(null);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setPages(landingPageService.getStoreLPs(storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
  };

  const handleMagicGenerate = async () => {
    if (!formData.goal || formData.selectedProductIds.length === 0) {
      return alert("Bhai, goal aur products toh choose karo!");
    }

    setIsGenerating(true);
    const selectedProds = products.filter(p => formData.selectedProductIds.includes(p.id));
    const copy = await generateLandingPageCopy(formData.goal, selectedProds);
    setIsGenerating(false);

    if (copy) {
      const newLP: LandingPage = {
        id: 'lp-' + Date.now(),
        storeId,
        slug: formData.title.toLowerCase().replace(/\s+/g, '-') || 'campaign-' + Date.now(),
        title: formData.title || 'Untitled Campaign',
        heroHeadline: copy.heroHeadline,
        heroSubheadline: copy.heroSubheadline,
        benefits: copy.benefits,
        productIds: formData.selectedProductIds,
        ctaText: copy.ctaText,
        themeColor: formData.themeColor,
        status: 'DRAFT',
        createdAt: new Date().toISOString(),
        views: 0,
        conversions: 0
      };
      setGeneratedLP(newLP);
    }
  };

  const handlePublish = () => {
    if (!generatedLP) return;
    landingPageService.saveLP({ ...generatedLP, status: 'PUBLISHED' });
    setShowModal(false);
    setGeneratedLP(null);
    loadData();
  };

  const copyLink = (slug: string, id: string) => {
    const link = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">AI Landing Pages</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">High-Conversion Campaign Architect</p>
        </div>
        <button 
          onClick={() => { setShowModal(true); setGeneratedLP(null); }}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2"
        >
          <Sparkles className="h-4 w-4" /> Architect New Page
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center"><Layout className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Total Pages</p>
               <h3 className="text-3xl font-black text-gray-900">{pages.length}</h3>
            </div>
         </div>
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center"><Eye className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Campaign Views</p>
               <h3 className="text-3xl font-black text-gray-900">{pages.reduce((sum, p) => sum + p.views, 0)}</h3>
            </div>
         </div>
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center"><TrendingUp className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Avg. Conversion</p>
               <h3 className="text-3xl font-black text-gray-900">14.2%</h3>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
         {pages.length === 0 ? (
           <div className="p-32 text-center space-y-6 opacity-30">
              <Megaphone className="h-20 w-20 mx-auto" />
              <p className="font-black uppercase tracking-widest text-xs">Aapne abhi tak koi AI landing page nahi banaya hai.</p>
           </div>
         ) : (
           <table className="w-full text-left">
              <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                 <tr>
                    <th className="px-10 py-6">Campaign Page</th>
                    <th className="px-10 py-6">Traffic</th>
                    <th className="px-10 py-6">Conversions</th>
                    <th className="px-10 py-6 text-right">Magic Link</th>
                    <th className="px-10 py-6 text-right">Actions</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                 {pages.map(lp => (
                   <tr key={lp.id} className="group hover:bg-gray-50/50 transition-all">
                      <td className="px-10 py-8">
                         <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center"><Megaphone className="h-5 w-5" /></div>
                            <div>
                               <p className="font-black text-gray-900">{lp.title}</p>
                               <p className="text-[10px] text-gray-400 font-bold">/lp/{lp.slug}</p>
                            </div>
                         </div>
                      </td>
                      <td className="px-10 py-8">
                         <span className="text-lg font-black text-gray-900">{lp.views}</span>
                      </td>
                      <td className="px-10 py-8">
                         <span className="text-lg font-black text-emerald-600">{lp.conversions}</span>
                      </td>
                      <td className="px-10 py-8 text-right">
                         <button 
                           onClick={() => copyLink(lp.slug, lp.id)}
                           className={`p-3 rounded-xl transition-all ${copiedId === lp.id ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400 hover:text-indigo-600'}`}
                         >
                            {copiedId === lp.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                         </button>
                      </td>
                      <td className="px-10 py-8 text-right">
                         <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a href={`/lp/${lp.slug}`} target="_blank" className="p-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><ExternalLink className="h-4 w-4" /></a>
                            <button onClick={() => { landingPageService.deleteLP(lp.id); loadData(); }} className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="h-4 w-4" /></button>
                         </div>
                      </td>
                   </tr>
                 ))}
              </tbody>
           </table>
         )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-4xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300 flex flex-col h-[90vh]">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                    <Sparkles className="h-6 w-6 text-indigo-600" /> AI Landing Page Generator
                 </h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>

              <div className="flex-grow overflow-y-auto p-10">
                 {!generatedLP ? (
                    <div className="max-w-2xl mx-auto space-y-10">
                       <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Campaign Name</label>
                          <input 
                            value={formData.title} 
                            onChange={e => setFormData({...formData, title: e.target.value})} 
                            placeholder="e.g. iPhone 16 Launch Sale" 
                            className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                          />
                       </div>
                       <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Campaign Goal (Describe your strategy)</label>
                          <textarea 
                            rows={3}
                            value={formData.goal} 
                            onChange={e => setFormData({...formData, goal: e.target.value})} 
                            placeholder="e.g. Focus on exclusive features and 24-hour delivery to boost impulse buys." 
                            className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold resize-none" 
                          />
                       </div>
                       <div className="space-y-4">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Featured Products</label>
                          <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto pr-2 scrollbar-hide">
                             {products.map(p => (
                               <button 
                                 key={p.id}
                                 onClick={() => setFormData(prev => ({
                                   ...prev,
                                   selectedProductIds: prev.selectedProductIds.includes(p.id) 
                                     ? prev.selectedProductIds.filter(id => id !== p.id)
                                     : [...prev.selectedProductIds, p.id]
                                 }))}
                                 className={`p-4 rounded-2xl border-2 flex items-center justify-between transition-all ${formData.selectedProductIds.includes(p.id) ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                               >
                                  <div className="flex items-center gap-3 text-sm font-bold">
                                     <img src={p.images[0]} className="w-8 h-8 rounded-lg object-cover" alt="" />
                                     {p.name}
                                  </div>
                                  {formData.selectedProductIds.includes(p.id) && <Check className="h-4 w-4 text-indigo-600" />}
                               </button>
                             ))}
                          </div>
                       </div>
                       <button 
                         onClick={handleMagicGenerate}
                         disabled={isGenerating}
                         className="w-full py-6 bg-indigo-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3"
                       >
                          {isGenerating ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Sparkles className="h-6 w-6" /> Generate High-Convert Page</>}
                       </button>
                    </div>
                 ) : (
                    <div className="space-y-12 animate-in fade-in duration-500">
                       <div className="p-8 bg-indigo-50 rounded-[3rem] border border-indigo-100 text-center space-y-4">
                          <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">Previewing AI Content</p>
                          <h2 className="text-4xl font-black text-gray-900 tracking-tight">{generatedLP.heroHeadline}</h2>
                          <p className="text-lg text-indigo-700 font-medium">{generatedLP.heroSubheadline}</p>
                       </div>
                       
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                          {generatedLP.benefits.map((b, i) => (
                            <div key={i} className="p-6 bg-white border border-gray-100 rounded-3xl shadow-sm space-y-2">
                               <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-black">{i+1}</div>
                               <h4 className="font-black text-gray-900">{b.title}</h4>
                               <p className="text-xs text-gray-500 font-medium leading-relaxed">{b.description}</p>
                            </div>
                          ))}
                       </div>

                       <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Call to Action: <span className="text-gray-900">{generatedLP.ctaText}</span></p>
                          <div className="flex gap-4">
                             <button onClick={() => setGeneratedLP(null)} className="text-xs font-black text-indigo-600 uppercase">Regenerate</button>
                             <button onClick={handlePublish} className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-indigo-700 transition-all">Publish Live Page</button>
                          </div>
                       </div>
                    </div>
                 )}
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminLandingPages;
